import React, { useState, useEffect } from 'react';
import { Row, Col, Modal } from 'react-bootstrap';
import { useNavigate, useLocation, Link } from 'react-router';
import { useDispatch } from 'react-redux';
import PageTitle from '../headers/PageTitle';
import MobileWarningDialog from '../DesignSystems/MobileWarning/MobileWarningDialog';
import { StylishNewToggleButtonGroup } from '../DesignSystems/New/StylishNewToggleButtonGroup';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import OrganizationResourcesView from './Resources/OrganizationResourcesView';
import ResourceKanban from './Resources/ResourceKanban/ResourceKanban';
import CheckInTable from './Resources/Tables/CheckInTable';
import CheckOutTable from './Resources/Tables/CheckOutTable';
import ResourceRequestsKanban from './ResourceRequests/ResourceRequestsKanban';
import {
  useResourcesInIncident,
  useCheckInResource,
  useUpdateResourceCheckIn,
  useUpdateResourceStatus,
  useCheckOutResource,
  useDeleteResourceInIncident,
  useManualCheckIn,
  useUpdateDemob,
  ResourceInIncident,
} from './hooks/resourceInIncidentHooks';
import { useCurrentUserApprovalAuthority } from '../../hooks/incident/useCurrentUserApprovalAuthority';
import ApprovalAuthorityWarning from '../DesignSystems/ApprovalAuthorityWarning';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useIncident } from './hooks/incidentHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { PremiumResourceManagementModal } from 'components/IAP/PremiumResourceManagementModal';
import { usePremiumResourceManagementFlag } from 'utils/featureFlags';

type ResourceView =
  | 'organization'
  | 'incident-kanban'
  | 'resource-requests'
  | 'checkin-table'
  | 'checkout-table';

interface CancelResource {
  id: string;
  resource_name?: string;
  current_status: string;
}

const IAPResources: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { exportAsForm } = useIcsFormPdfPreview();
  const premiumResourceManagement = usePremiumResourceManagementFlag();
  const [currentView, setCurrentView] =
    useState<ResourceView>('incident-kanban');
  const [cancelResource, setCancelResource] = useState<CancelResource | null>(
    null
  );
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Extract view from URL path
  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];

    switch (lastSegment) {
      case 'org':
        setCurrentView('organization');
        break;
      case 'incident':
        setCurrentView('incident-kanban');
        break;
      case 'requests':
        if (!premiumResourceManagement) {
          setShowUpgradeModal(true);
          navigate(
            location.pathname.replace(/\/requests$/, '') + '/incident',
            { replace: true }
          );
          break;
        }
        setCurrentView('resource-requests');
        break;
      case 'check-in':
        if (!premiumResourceManagement) {
          navigate(
            location.pathname.replace(/\/check-in$/, '') + '/incident',
            { replace: true }
          );
          break;
        }
        setCurrentView('checkin-table');
        break;
      case 'check-out':
        if (!premiumResourceManagement) {
          navigate(
            location.pathname.replace(/\/check-out$/, '') + '/incident',
            { replace: true }
          );
          break;
        }
        setCurrentView('checkout-table');
        break;
      case 'resources':
        // Default view when at base /resources path
        setCurrentView('incident-kanban');
        break;
      default:
        // If no specific sub-route, default to incident-kanban
        if (pathSegments[pathSegments.length - 2] === 'resources') {
          setCurrentView('incident-kanban');
        }
        break;
    }
  }, [location.pathname, navigate, premiumResourceManagement]);

  // Handle view change and update URL
  const handleViewChange = (newView: ResourceView) => {
    if (
      !premiumResourceManagement &&
      (newView === 'resource-requests' ||
        newView === 'checkin-table' ||
        newView === 'checkout-table')
    ) {
      if (newView === 'resource-requests') {
        setShowUpgradeModal(true);
      }
      return;
    }
    setCurrentView(newView);
    const basePath = location.pathname.replace(
      /\/(org|incident|requests|check-in|check-out)$/,
      ''
    );

    switch (newView) {
      case 'organization':
        navigate(`${basePath}/org`);
        break;
      case 'incident-kanban':
        navigate(`${basePath}/incident`);
        break;
      case 'resource-requests':
        navigate(`${basePath}/requests`);
        break;
      case 'checkin-table':
        navigate(`${basePath}/check-in`);
        break;
      case 'checkout-table':
        navigate(`${basePath}/check-out`);
        break;
    }
  };

  // Data hooks
  const resourcesInIncident = useResourcesInIncident();
  const hasApprovalAuthority = useCurrentUserApprovalAuthority();

  const checkInMutation = useCheckInResource();
  const updateCheckInMutation = useUpdateResourceCheckIn();
  const updateStatusMutation = useUpdateResourceStatus();
  const checkOutMutation = useCheckOutResource();
  const deleteMutation = useDeleteResourceInIncident();
  const manualCheckInMutation = useManualCheckIn();
  const updateDemobMutation = useUpdateDemob();

  // Handle resource updates from kanban
  const handleResourceUpdate = async (
    updatedResource: Partial<ResourceInIncident>
  ) => {
    // Determine update type based on the data
    if (updatedResource.checkin_datetime && !updatedResource.id) {
      // This is a new check-in
      await checkInMutation.mutateAsync(updatedResource);
    } else if (
      updatedResource.checkout_datetime &&
      updatedResource.checkout_datetime !== null &&
      updatedResource.id
    ) {
      // This is a check-out (inferred from data)
      await checkOutMutation.mutateAsync({
        id: updatedResource.id,
        data: updatedResource,
      });
    } else if (updatedResource.id && hasCheckInFields(updatedResource)) {
      // This is a check-in update (has check-in specific fields)
      await updateCheckInMutation.mutateAsync({
        id: updatedResource.id,
        data: updatedResource,
      });
    } else if (updatedResource.id) {
      // This is a status update
      await updateStatusMutation.mutateAsync({
        id: updatedResource.id,
        data: updatedResource,
      });
    }
  };

  /**
   * A resource documented at the desk rather than dispatched. The server creates the asset and the check-in
   * together, so what comes back is an ordinary resource the rest of the tab already knows how to show.
   */
  const handleManualCheckIn = async (data: Partial<ResourceInIncident>) => {
    await manualCheckInMutation.mutateAsync(data);
  };

  /** Amend a demobilization already under way, writing only the fields the drawer sent. */
  const handleDemobUpdate = async (
    id: string,
    data: Partial<ResourceInIncident>
  ) => {
    await updateDemobMutation.mutateAsync({ id, data });
  };

  // Helper to detect if update contains check-in fields
  const hasCheckInFields = (data: Partial<ResourceInIncident>): boolean => {
    const checkInFields = [
      'leader_name',
      'personnel_count',
      'order_request_number',
      'state_and_agency',
      'resource_category',
      'resource_kind',
      'resource_type',
      'home_unit_agency',
      'departure_point',
      'departure_datetime',
      'travel_method',
    ];
    return checkInFields.some((field) => field in data);
  };

  // Handle resource cancellation (only for in-transit resources)
  const handleResourceCancel = (resource: CancelResource) => {
    if (resource.current_status === 'in_transit' && resource.id) {
      setCancelResource(resource);
      setShowCancelConfirm(true);
    }
  };

  // Confirm cancellation
  const confirmCancel = async () => {
    if (cancelResource?.id) {
      await deleteMutation.mutateAsync(cancelResource.id);
      setShowCancelConfirm(false);
      setCancelResource(null);
    }
  };

  // Handle export of ICS 211-CG Check-In List
  const handleExportCheckedIn = () => {
    void exportAsForm({ formname: 'ICS_211_CG', mapSketch: null });
  };

  const checkedOutResources = (resourcesInIncident.data || []).filter(
    (r) => r.checkout_datetime
  );

  const incident = useIncident();
  const incidentName = incident.data?.name;

  const pageTitleMarginOffset = '-0.75rem';

  return (
    <MobileWarningDialog>
      <div className="maincontent">
        <div className="d-flex align-items-center justify-content-start">
          <PageTitle title="Resources" />
          <Link to="../dashboard">
            <StylishNewButton
              className="button button--primary ms-3"
              style={{ marginTop: pageTitleMarginOffset }}
            >
              <SharedIcon iconName="person" classes="me-2" />
              Return to Incident {incidentName}
            </StylishNewButton>
          </Link>
        </div>
        {/* Single button group navigation */}
        <Row className="mb-4">
          <Col>
            <StylishNewToggleButtonGroup
              data={[
                { label: 'Resource Requests', value: 'resource-requests' },
                { label: 'Organization Resources', value: 'organization' },
                { label: 'Incident Resources', value: 'incident-kanban' },
                ...(premiumResourceManagement
                  ? [
                      {
                        label: 'Check-In (ICS 211)',
                        value: 'checkin-table',
                      },
                      {
                        label: 'Check-Out (ICS 238)',
                        value: 'checkout-table',
                      },
                    ]
                  : []),
              ]}
              selected={currentView}
              onChange={(val) => handleViewChange(val as ResourceView)}
            />
          </Col>
        </Row>

        {/* Approval Authority Warning for Resource Management */}
        {!hasApprovalAuthority &&
          (currentView === 'incident-kanban' ||
            currentView === 'checkin-table' ||
            currentView === 'checkout-table') && (
            <Row className="mb-3">
              <Col>
                <ApprovalAuthorityWarning
                  message="You don't have approval authority for resource management actions (check-in, check-out approvals). Some features may be restricted."
                  variant="info"
                />
              </Col>
            </Row>
          )}

        {/* Content based on current view */}
        {currentView === 'organization' && <OrganizationResourcesView />}

        {currentView === 'incident-kanban' && (
          <ResourceKanban
            resources={resourcesInIncident.data || []}
            isLoading={resourcesInIncident.isLoading}
            onResourceUpdate={handleResourceUpdate}
            onResourceCancel={handleResourceCancel}
            onExportCheckedIn={
              premiumResourceManagement ? handleExportCheckedIn : undefined
            }
            onManualCheckIn={handleManualCheckIn}
            onDemobUpdate={handleDemobUpdate}
          />
        )}

        {currentView === 'resource-requests' &&
          premiumResourceManagement && <ResourceRequestsKanban />}

        {currentView === 'checkin-table' && (
          <CheckInTable
            resources={resourcesInIncident.data || []}
            isLoading={resourcesInIncident.isLoading}
            onStatusUpdate={(resource) => handleResourceUpdate(resource)}
            onExportCheckedIn={handleExportCheckedIn}
          />
        )}

        {currentView === 'checkout-table' && (
          <CheckOutTable
            resources={checkedOutResources}
            isLoading={resourcesInIncident.isLoading}
          />
        )}

        <PremiumResourceManagementModal
          show={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />

        {/* Cancel Confirmation Modal */}
        <Modal
          show={showCancelConfirm}
          onHide={() => setShowCancelConfirm(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Cancel Resource Assignment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Are you sure you want to cancel the assignment for{' '}
              <strong>{cancelResource?.resource_name}</strong>?
            </p>
            <p className="text-muted">
              This will permanently remove the resource from the incident.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <StylishNewButton
              className="button button--link"
              onClick={() => setShowCancelConfirm(false)}
            >
              Keep Assignment
            </StylishNewButton>
            <StylishNewButton
              className="button button--primary bg-danger border-danger"
              onClick={confirmCancel}
            >
              Cancel Assignment
            </StylishNewButton>
          </Modal.Footer>
        </Modal>
      </div>
    </MobileWarningDialog>
  );
};

export default IAPResources;
